import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Globe, Zap, Trophy, ChevronRight, ChevronLeft, Maximize2, X } from 'lucide-react';

const INK = 'var(--text-primary)';
const STEEL = 'var(--text-secondary)';
const RED = '#FF8618';

/* SEE WHAT'S NEW feature cards */
const FEATURE_CARDS = [
  { id: 'terraform', title: 'Terraform System', icon: Globe, image: '/backgrounds/Terraform System.png', description: "Transform the environment to match your race's home planet. Gain tactical advantages while opponents struggle in hostile conditions.", cta: 'Explore Terraforming' },
  { id: 'plasma', title: 'Plasma Reactor', icon: Zap, image: '/backgrounds/plasma-reactor-gun.png', description: 'Advanced energy system that powers plasma weapons and utilities. Manage your energy strategically for maximum combat effectiveness.', cta: 'Manage Energy' },
  { id: 'royale', title: 'Battle Royale', icon: Trophy, image: '/backgrounds/Battle Royale.png', description: '25-player intergalactic showdown. Solo, Duos, or Squads. Last team standing wins the war for planet E-47.', cta: 'Join the Fight' },
];

const Features = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const triggerRefs = useRef([]);

  const openAt = (i) => setOpenIndex(i);
  const close = () => {
    setOpenIndex((current) => {
      triggerRefs.current[current]?.focus();
      return null;
    });
  };
  const goPrev = () => setOpenIndex((i) => (i - 1 + FEATURE_CARDS.length) % FEATURE_CARDS.length);
  const goNext = () => setOpenIndex((i) => (i + 1) % FEATURE_CARDS.length);

  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative pt-14 pb-16 px-6 overflow-hidden"
    >
      {/* Top-right angular tech lines */}
      <div className="absolute top-0 right-0 w-[60%] h-[420px] pointer-events-none" style={{ opacity: 0.5 }}>
        <svg viewBox="0 0 800 400" className="w-full h-full" preserveAspectRatio="none">
          <path d="M800 60 L520 60 L470 110 L800 110" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="1.5" />
          <path d="M800 130 L560 130 L520 170 L800 170" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1.5" />
          <path d="M800 200 L620 200 L590 230 L800 230" fill="none" stroke="rgba(255,134,24,0.12)" strokeWidth="1.5" />
        </svg>
      </div>

      {/* Subtle readability gradient behind the section only — no visible panel */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, rgba(3,7,11,0.25) 0%, transparent 25%, transparent 75%, rgba(3,7,11,0.25) 100%)' }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* ===== SEE WHAT'S NEW ===== */}
        <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 items-start mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs uppercase tracking-[0.45em]" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, color: RED }}>Mechanics</span>
              <div className="flex items-center gap-1">
                <span className="block h-[2px] w-6" style={{ background: RED }} />
                <span className="block h-[2px] w-3" style={{ background: RED }} />
                <span className="block h-[2px] w-2" style={{ background: 'rgba(255,134,24,0.5)' }} />
              </div>
            </div>
            <h2 className="font-black uppercase leading-[0.92] mb-5" style={{ fontFamily: '"Russo One", "Black Ops One", sans-serif', fontWeight: 900, fontSize: 'clamp(2.25rem, 5.2vw, 4rem)', letterSpacing: '0.02em' }} data-testid="features-title">
              <span style={{ color: INK, textShadow: '0 1px 0 rgba(255,255,255,0.6)' }}>SEE </span>
              <span style={{ color: RED, textShadow: '0 2px 14px rgba(255,134,24,0.25)' }}>WHAT&apos;S NEW</span>
            </h2>
            <p className="max-w-xl text-base leading-relaxed" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 500, color: STEEL }}>
              Discover the latest features and gameplay mechanics built to deliver a deeper, more intense battle experience.
            </p>
          </div>
          <div className="flex md:justify-end md:pt-2">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <span className="block h-[2px] w-2" style={{ background: 'rgba(255,134,24,0.5)' }} />
                <span className="block h-[2px] w-3" style={{ background: RED }} />
                <span className="block h-[2px] w-6" style={{ background: RED }} />
              </div>
              <span className="text-xs uppercase tracking-[0.45em]" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, color: 'var(--text-muted)' }}>Latest Features</span>
            </div>
          </div>
        </div>

        {/* Subtle instruction hint */}
        <div className="flex items-center justify-center gap-2 mb-6" data-testid="features-instruction">
          <Maximize2 className="w-3 h-3" style={{ color: RED, opacity: 0.85 }} />
          <span className="text-xs" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 500, color: 'var(--text-muted)' }}>
            Select an image to view it in full detail.
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10">
          {FEATURE_CARDS.map((card, i) => (
            <FeatureCard
              key={card.id}
              card={card}
              onOpen={() => openAt(i)}
              triggerRef={(el) => (triggerRefs.current[i] = el)}
            />
          ))}
        </div>
      </div>

      {openIndex !== null && (
        <FeatureImageModal
          card={FEATURE_CARDS[openIndex]}
          onClose={close}
          onPrev={goPrev}
          onNext={goNext}
        />
      )}
    </section>
  );
};

const FeatureCard = ({ card, onOpen, triggerRef }) => {
  const Icon = card.icon;

  return (
    <div
      data-testid={`feature-card-${card.id}`}
      className="relative flex flex-col h-full"
    >
      <div className="flex items-start gap-4 mb-5">
        <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center" style={{ clipPath: 'none', borderRadius: '3px', background: 'rgba(255,134,24,0.10)', border: '1px solid rgba(255,122,0,0.45)' }}>
          <Icon className="w-5 h-5" style={{ color: RED }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-black uppercase leading-[0.95]" style={{ fontFamily: '"Russo One", "Black Ops One", sans-serif', fontWeight: 900, color: INK, fontSize: 'clamp(1.2rem, 2vw, 1.6rem)', letterSpacing: '0.02em' }}>
            {card.title.split(' ').map((w, i) => (
              <React.Fragment key={i}>{w}{i < card.title.split(' ').length - 1 && <br />}</React.Fragment>
            ))}
          </h3>
          <span className="block mt-2 h-[2px] w-10" style={{ background: RED, boxShadow: '0 0 8px rgba(255,134,24,0.5)' }} />
        </div>
      </div>

      <button
        ref={triggerRef}
        type="button"
        onClick={onOpen}
        aria-label={`View ${card.title} image`}
        data-testid={`feature-image-${card.id}`}
        className="feature-image-clickable w-full h-48 lg:h-52 mb-5"
      >
        <img
          src={card.image}
          alt={card.title}
          className={card.id === 'plasma' ? 'plasma-reactor-image' : undefined}
          style={card.id === 'plasma' ? {} : { objectPosition: 'center' }}
        />
        <div className="feature-image-overlay">
          <Maximize2 className="feature-image-overlay-icon" />
          <span className="feature-image-overlay-title">Click to View</span>
          <span className="feature-image-overlay-subtitle">View full image</span>
        </div>
        <div className="feature-touch-expand" aria-hidden="true">
          <Maximize2 className="w-3.5 h-3.5" style={{ color: RED }} />
        </div>
      </button>

      <p className="text-sm leading-relaxed mb-6 flex-1" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 500, color: STEEL }}>{card.description}</p>

      <button data-testid={`feature-cta-${card.id}`} className="feature-cta-link group relative inline-flex items-center gap-2 mt-auto self-start" style={{ background: 'transparent', border: 'none', padding: 0 }}>
        <span className="text-xs uppercase tracking-[0.3em]" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, color: RED }}>{card.cta}</span>
        <ChevronRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" style={{ color: RED }} />
      </button>
    </div>
  );
};

const FeatureImageModal = ({ card, onClose, onPrev, onNext }) => {
  const closeBtnRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtnRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext]);

  return createPortal(
    <div
      className="feature-lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`${card.title} image viewer`}
      onClick={onClose}
      data-testid="feature-lightbox"
    >
      <div className="feature-lightbox-content" onClick={(e) => e.stopPropagation()}>
        <button ref={closeBtnRef} type="button" className="feature-lightbox-close" onClick={onClose} aria-label="Close image viewer">
          <X className="w-5 h-5" />
        </button>
        <button type="button" className="feature-lightbox-nav feature-lightbox-prev" onClick={onPrev} aria-label="Previous image">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <img src={card.image} alt={card.title} className="modal-image" />
        <button type="button" className="feature-lightbox-nav feature-lightbox-next" onClick={onNext} aria-label="Next image">
          <ChevronRight className="w-5 h-5" />
        </button>
        <div className="feature-lightbox-title">{card.title}</div>
      </div>
    </div>,
    document.body
  );
};

export default Features;
